import { CollectionStatus } from '@share/enums';
import { CountdownRenderProps } from 'react-countdown';
import { Link, useParams } from 'react-router-dom';
import { css } from '@emotion/react';
import { useAPI, useCurrency, useHelper } from 'src/hooks';
import { useMemo, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import BlurQRCode from '@asset/images/blur-qrcode.webp';
import Spinner from '@com/utils/Spinner';
import loadable from '@loadable/component';
import styled from '@emotion/styled';

const SkeletonImage = loadable(() => import('@com/utils/SkeletonImage'));
const MouseOver = loadable(() => import('@com/utils/MouseOver'));
const Countdown = loadable(() => import('react-countdown'));

interface ImgProps {
  src?: string;
  currentPrice?: number;
  endAt?: number;
}

interface Props {
  data: any;
  isShowOwner?: boolean;
  isAllowToFlip?: boolean;
}

const FlipCard = styled.div`
  position: absolute;
  width: 100%;
  margin-top: -100%;
  -webkit-backface-visibility: hidden; /* Safari */
  backface-visibility: hidden;
`;

const CardBack = styled(FlipCard)`
  border-radius: 16px;
  height: 100%;
  background-color: var(--primary-color9);
  transform: rotateY(180deg);
`;

const QRCodeImg = styled.img`
  border-radius: 16px;
`;

const GetQRCodeBtn = styled.button`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: var(--primary-color3);
  color: #fff;
  box-shadow: 4px 4px 4px 0px rgba(0, 0, 0, 0.75);
  :hover {
    color: #fff;
  }
`;

const FlipBtn = styled.button`
  border-radius: 8px;
  position: absolute;
  bottom: 5%;
  right: 5%;
  background-color: var(--primary-color3);
  color: #fff;
  padding: 8px;
  font-size: 32px;
  opacity: 0.9;

  :hover {
    color: #fff;
  }
`;

const TokenTag = styled.div`
  line-height: 12px;
`;

const HiddenCheckbox = styled.input`
  display: none !important;
  :checked + div {
    transform: rotateY(180deg);
  }
`;

const CardContainer = styled.div`
  background-color: var(--bg-section2);
`;

const FlipCardContainer = styled.div`
  position: relative;
  width: 100%;
  padding-top: 100%; /* 1:1 Aspect Ratio */
  transition: transform 0.8s;
  transform-style: preserve-3d;
  margin-bottom: 21px;
`;

function BidCountdown({ endAt }: { endAt: number }) {
  const { t } = useTranslation();
  return (
    <div className="featured-countdown">
      <span className="slogan" />
      <Countdown
        date={endAt * 1000}
        renderer={(props: CountdownRenderProps) => {
          return (
            <span>{`${props.days ? `${props.days}:` : ''}${props.hours}:${
              props.minutes
            }:${props.seconds}`}</span>
          );
        }}
      >
        <span>{t('general.bidEnd')}</span>
      </Countdown>
    </div>
  );
}

function ProductImg(props: ImgProps) {
  const { src, currentPrice, endAt } = props;
  const { t } = useTranslation();

  const isEnded = new Date().getTime() / 1000 > Number(endAt);

  return (
    <>
      <SkeletonImage
        alt="Product"
        extraStyle={css`
          object-fit: contain !important;
        `}
        ratio={1}
        size={[256, 512]}
        src={src}
      />
      {!isEnded && (
        <div className="coming-soon">
          {currentPrice ? t('general.onBid') : t('general.onSale')}
        </div>
      )}
      {endAt && !isEnded ? <BidCountdown endAt={endAt} /> : ''}
    </>
  );
}

function ProductCard(props: Props): JSX.Element {
  /* Define state / hook */
  const { data, isShowOwner = false, isAllowToFlip = false } = props;

  const { t } = useTranslation();
  const { collectionIdentifier } = useParams(); // Collection details page's card requried
  const { displayName, getUserIdentifier, displayCurrency } = useHelper();
  const { getCurrencyNameById } = useCurrency();
  const { getNftQRCode } = useAPI();
  const [isShowQRCode, setIsShowQRCode] = useState(false);

  // Use ref istead of state to prevent re-rendering when qrCodeURL changes
  const isFilpped = useRef(false);
  const checkbox = useRef<HTMLInputElement>(null);

  /* Custom Data Object for render or other usages */
  const order = data?.order?.[0];
  const owner = data.owner[0];
  const collection = data?.collection;
  const orderDetail = order?.order_info;
  const currencyId = order?.currency?.id;
  const chainId = data?.network_chain_id;

  const finalCollectionId =
    collection?.custom_url || collection?.address || collectionIdentifier;

  const { data: qrCodeURL, isLoading } = useQuery(
    [getNftQRCode.name, chainId, collection?.address, data?.token_id],
    () => {
      return getNftQRCode(chainId, collection?.address, data?.token_id);
    },
    {
      enabled: isShowQRCode,
      refetchInterval: 15 * 60 * 1000,
    }
  );

  /* Render control function / logic */

  /* Data Change Listeners - useEffect/useMemo/useCallback */

  /* Styling */

  const CardFront = useMemo(
    () => styled(FlipCard)`
      /* FIXME: Mobile issue, will suddently show front image */
      z-index: ${isFilpped ? '0' : '-100'};
      background-color: var(--bg-section2);
    `,
    [isFilpped]
  );

  /* Render */
  return (
    <div
      className={`h-100 mb-0 sc-card-product explode style2 mg-bt ${
        orderDetail ? 'comingsoon' : ''
      } `}
    >
      {isAllowToFlip ? (
        <>
          <HiddenCheckbox
            ref={checkbox}
            defaultChecked={isFilpped.current}
            type="checkbox"
          />
          <FlipCardContainer>
            <CardFront className="card-media mb-0">
              <Link
                to={`/nft/${chainId}/${finalCollectionId}/${data?.token_id}`}
              >
                <ProductImg
                  currentPrice={orderDetail?.current_price}
                  endAt={Number(orderDetail?.end_at_timestamp)}
                  src={data?.preview || data?.asset}
                />
              </Link>
              <FlipBtn
                className="sc-button"
                onClick={() => {
                  checkbox?.current?.click();
                  isFilpped.current = true;
                }}
              >
                <i aria-hidden className="fa fa-qrcode" />
              </FlipBtn>
            </CardFront>
            <CardBack>
              {/* No SkeletonImage to prevent flickering when updating state */}
              <QRCodeImg
                alt="QRCode"
                loading="lazy"
                src={isShowQRCode && qrCodeURL ? qrCodeURL : BlurQRCode}
                onClick={() => {
                  checkbox?.current?.click();
                  isFilpped.current = false;
                }}
              />
              {(!isShowQRCode || isLoading) && (
                <GetQRCodeBtn
                  className="d-flex align-items-center"
                  onClick={() => {
                    setIsShowQRCode(true);
                  }}
                >
                  <span>{t('collection.view')}</span>
                  <Spinner
                    className="ml-2"
                    color="#fff"
                    width={isLoading ? 24 : 0}
                  />
                </GetQRCodeBtn>
              )}
            </CardBack>
          </FlipCardContainer>
        </>
      ) : (
        <CardContainer className="card-media">
          <Link to={`/nft/${chainId}/${finalCollectionId}/${data?.token_id}`}>
            <ProductImg
              currentPrice={orderDetail?.current_price}
              endAt={Number(orderDetail?.end_at_timestamp)}
              src={data?.preview || data?.asset}
            />
          </Link>
        </CardContainer>
      )}
      <Link to={`/nft/${chainId}/${finalCollectionId}/${data?.token_id}`}>
        <MouseOver text={data?.name}>
          <div className="card-title mg-bt-16">
            <h5>{data?.name}</h5>
          </div>
        </MouseOver>
      </Link>
      <div className="meta-info mb-4">
        {isShowOwner ? (
          <div className="author overflow-hidden">
            <div className="avatar">
              <SkeletonImage
                alt="Avatar"
                ratio={1}
                size={[64, 128]}
                src={owner?.avatar}
              />
            </div>
            <div className="info text-left d-flex flex-column flex-grow-1 overflow-hidden">
              <span>{t('collection.owner')}</span>
              <h6 className="text-truncate">
                <Link to={`/profile/${getUserIdentifier(owner)}`}>
                  {displayName(owner?.name)}
                </Link>
              </h6>
            </div>
          </div>
        ) : (
          <div className="author overflow-hidden">
            <div className="avatar mr-4">
              <SkeletonImage
                ratio={1}
                size={[64, 128]}
                src={collection?.details?.image?.icon || collection?.logo_image}
              />
            </div>
            <div className="info text-left d-flex flex-column flex-grow-1 overflow-hidden">
              <span>{t('live.collection')}</span>
              <h6 className="text-truncate">
                {collection?.status === CollectionStatus.LISTING ? (
                  <Link to={`/collection/${chainId}/${finalCollectionId}`}>
                    {collection?.name}
                  </Link>
                ) : (
                  collection?.name
                )}
              </h6>
            </div>
          </div>
        )}
        <TokenTag className="tags px-4">{data?.token_id}</TokenTag>
      </div>
      {orderDetail && (
        <div className="d-flex card-bottom style-explode m-0 mt-4">
          <div className="price">
            <span className="fs-18">
              {orderDetail?.current_price
                ? t('general.lastestBid')
                : t('general.price')}
              <b className="ml-2">
                {displayCurrency(
                  orderDetail?.current_price === '0'
                    ? orderDetail?.minimum_bid
                    : orderDetail?.current_price || orderDetail?.selling_price,
                  getCurrencyNameById(currencyId)
                )}
              </b>
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductCard;
